/**
 * **校准余额**（`DR-056` · 批次 96）—— 两种方式二选一
 *
 * ## 🔴 这个对话框存在的理由
 *
 * 在此之前，「事后校准余额」是**一条不存在的路**：`AccountForm` 里写着
 * 「事后校准请记一笔「调整」」，而「调整」既没有分类也没有入口。
 * 用户数了数钱包发现对不上，只能去建一笔真支出 —— 那会污染支出统计。
 *
 * ## 两种方式（`DR-056` D1，**同一次只能选一种**）
 *
 * | 方式 | 干什么 | 之后改「校准之前」的交易 |
 * |---|---|---|
 * | **记差额**（默认） | 写一笔「不计收支」的调整腿 | 余额**跟着变** |
 * | **锁定基准** | 同上 **+** 记一个锚点 | 余额**纹丝不动** |
 *
 * ## 🔴 为什么「锁定基准」必须显式警告（不是 tooltip，是正文）
 *
 * 锁定之后，改锚点之前的交易：那笔金额**仍进流水与报表**，但余额不动 ⇒
 * 「报表支出合计」与「现金减少」会对不上，而且**没有一条记录解释这个差**。
 * 沉默地提供一个会制造账实不符的开关，比不提供更糟（`DR-056` D7）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BalanceAdjustMode,
  currencySymbolOf,
  toCents,
  type AccountView,
} from '@jiaying/shared';
import { adjustAccountBalance } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  adjustDeltaOf,
  adjustIssueOf,
  canSubmitAdjust,
  defaultAdjustText,
} from '../features/adjust-balance.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';
import { Amount } from './Amount.js';

export interface AdjustBalanceDialogProps {
  /** 目标账户（必是 `cash` / `debit` / `ewallet` —— 调用方负责只给这三类） */
  account: AccountView;
  onClose: () => void;
}

export function AdjustBalanceDialog({ account, onClose }: AdjustBalanceDialogProps): ReactElement {
  const qc = useQueryClient();
  const sym = currencySymbolOf(account.currency);
  const current = account.balance as number;

  /** 默认填当前余额 —— 用户只需要改那几个不对的数字，不必从 0 开始敲 */
  const [targetText, setTargetText] = useState(() => defaultAdjustText(current));
  const [mode, setMode] = useState<BalanceAdjustMode>(BalanceAdjustMode.Difference);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const targetCents = yuanTextToCentsOrNull(targetText);
  // 🔴 三条判据全在 `features/adjust-balance.ts`（对话框只负责摆值，见该文件头）
  const delta = adjustDeltaOf(targetText, current);
  const issue = adjustIssueOf(targetText, current);
  const formatIssue = targetText.trim() === '' ? undefined : (issue ?? undefined);
  const zeroDelta = delta === 0;

  const save = useMutation({
    mutationFn: (): Promise<unknown> =>
      adjustAccountBalance(account.id, {
        targetBalance: targetCents as number,
        mode,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: () => {
      // 余额变了 ⇒ 这四处的缓存都要失效（与账户编辑同一批，避免"改完看到旧数"）
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['accountLedger'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onClose();
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const canSubmit = canSubmitAdjust(targetText, current, save.isPending);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>校准余额 · {account.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="caption" color="text.secondary">
              当前余额
            </Typography>
            <Typography variant="h6" data-testid="adjust-current">
              <Amount cents={toCents(current)} currency={account.currency} />
            </Typography>
          </Stack>

          <TextField
            size="small"
            required
            inputMode="decimal"
            label={sym === '' ? '实际余额' : `实际余额（${sym}）`}
            helperText={formatIssue ?? '数了数钱，现在实际是多少'}
            error={formatIssue !== undefined}
            value={targetText}
            onChange={(e) => {
              setTargetText(e.target.value);
              setError(null);
            }}
            inputProps={{ 'data-testid': 'adjust-target' }}
          />

          {/* 差额实时回显 —— 用户改数字时立刻看到"这次要补/要减多少" */}
          {delta !== null && !zeroDelta && (
            <Box data-testid="adjust-delta">
              <Typography variant="body2" color="text.secondary">
                差额：
                <Amount cents={toCents(delta)} currency={account.currency} signed />
                （{delta > 0 ? '余额变多' : '余额变少'}）
              </Typography>
            </Box>
          )}
          {zeroDelta && targetText.trim() !== '' && (
            <Typography variant="caption" color="text.secondary" data-testid="adjust-zero">
              余额已经一致，无需校准
            </Typography>
          )}

          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value as BalanceAdjustMode)}
            data-testid="adjust-mode"
          >
            <FormControlLabel
              value={BalanceAdjustMode.Difference}
              control={<Radio size="small" />}
              label="记差额（推荐）"
              data-testid="adjust-mode-difference"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              补一笔「不计收支」的调整，差额看得出、也能删。之后修改之前的交易，余额会跟着变。
            </Typography>
            <FormControlLabel
              value={BalanceAdjustMode.Anchor}
              control={<Radio size="small" />}
              label="锁定基准"
              data-testid="adjust-mode-anchor"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
              以这次的数字为准。之后无论怎么改之前记的账，余额都固定在这个值。
            </Typography>
          </RadioGroup>

          {/* 🔴 锁定模式必须显式告知代价（不能藏在 tooltip 里） */}
          {mode === BalanceAdjustMode.Anchor && (
            <Alert severity="warning" data-testid="adjust-anchor-warning">
              锁定后，修改这次校准**之前**的交易，那笔金额仍会进流水和报表，但余额不会再变 ——
              此时「报表支出合计」与「现金减少」可能对不上，这是锁定模式的正常代价。
            </Alert>
          )}

          <TextField
            size="small"
            label="备注（选填）"
            placeholder="例如：钱包数了数少 20"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ 'data-testid': 'adjust-note' }}
          />

          {error !== null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={!canSubmit}
          data-testid="adjust-submit"
        >
          {save.isPending ? '校准中…' : '确认校准'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
